import { type UrlReplacement } from '~/server/logic/playwright/run/types';
import { type Pattern } from './components/rrweb/RRWebPlayer';
import type {
  EmailFilters,
  OperationData,
  FetchRequestHeaders,
  TargetSchema,
} from './server/logic/steps/operation/types';
import type { EmailVariableExtraction } from './server/logic/steps/operation/types';
import { z } from 'zod';
import type { Region } from './server/logic/container/regions';
import type { JsonObject, JsonArray } from '@prisma/client/runtime/library';
import type { DashboardFilterType } from './common/dashboard/types';
import type { SuiteTriageReportWithPrompt } from './server/logic/playwright/triage-agent/triage-types';
import type { FullConfig, PlaywrightTestConfig } from 'playwright/test';
import type { EvaluatedConfig as EvaluatedConfigType } from './components/testConfig/types';
import type { SuiteAutoHealReport } from './server/logic/ai/autoheal-agent/autoheal-report';
import type { AutohealProgressState as AutohealProgressStateType } from './server/logic/playwright/auto-heal/autoheal-progress-store';

// https://github.com/arthurfiorette/prisma-json-types-generator
export {};

const zVariable = z.object({
  type: z.enum(['STATIC', 'DYNAMIC']),
  value: z.string(),
});

export const zVariables = z.record(z.string(), zVariable);
type VariablesType = z.output<typeof zVariables>;

export const zAutohealSuggestionDecision = z.enum(['pending', 'dismissed', 'approved']);

export const zAutohealHistoryStats = z.object({
  currentStreakLength: z.number(),
  currentStreakSuccessCount: z.number(),
  currentStreakSuccessRatio: z.number(),
  recentAutohealAttemptIds: z.array(z.string()),
});

export const zAutohealSuggestion = z.object({
  shouldSuggest: z.boolean(),
  stats: zAutohealHistoryStats,
  decision: zAutohealSuggestionDecision,
  computedAt: z.string(),
  attemptId: z.string(),
  inputTestContentId: z.string().nullable().optional(),
  analysisAttemptIds: z.array(z.string()),
});

export const zAiChange = z.object({
  stepId: z.string(),
  stepIndex: z.number(),
  stepDescription: z.string(),
  summary: z.string(),
  isSuccess: z.boolean(),
  erroredOperationId: z.string().optional(),
  firstAutohealOperationId: z.string().optional(),
});

export const zExecutionError = z.object({
  stepId: z.string(),
  stepIndex: z.number(),
  stepDescription: z.string(),
  operationId: z.string(),
  operationDescription: z.string(),
  error: z.string(),
});

export const zExecutionLog = z.object({
  stepId: z.string(),
  operationId: z.string().optional(),
  log: z.string(),
});

export const zConditionalBlockResult = z.object({
  conditionalBlockId: z.string(),
  // true for THEN, false for ELSE
  resolvedTo: z.boolean(),
  operationId: z.string(),
  stepId: z.string(),
  stepDescription: z.string(),
});

/**
 * Schema for logging internal operations performed by AI_ACTION operations.
 * Each log entry captures the details of a single action the AI performed
 * including screenshots (before/action/after), target element, and operation details.
 */
export const zAiActionLog = z.object({
  id: z.string(),
  parentOperationId: z.string(), // The AI_ACTION operation that triggered this
  stepId: z.string(), // Step containing the AI_ACTION
  description: z.string(), // Human-readable operation description
  targetElement: z
    .object({
      name: z.string(),
      locator: z.string(),
      boundingBox: z
        .object({
          x: z.number(),
          y: z.number(),
          width: z.number(),
          height: z.number(),
        })
        .optional(),
    })
    .optional(),
  beforeScreenshotUrl: z.string().optional(),
  actionScreenshotUrl: z.string().optional(),
  afterScreenshotUrl: z.string().optional(),
  operationType: z.string(), // ACTION, NAVIGATION, ASSERTION, etc.
  action: z.string().optional(), // click, fill, selectOption, etc. (for ACTION type)
  value: z.string().optional(), // Input value (text typed, URL navigated to)
  valueType: z.string().optional(), // STATIC, VARIABLE, etc.
  options: z.record(z.string(), z.unknown()).optional(), // Additional operation options
  order: z.number(), // Sequence within the AI action
  error: z.string().optional(), // Error message if operation failed
});

/**
 * Schema for operations that failed during execution but were eligible for auto-heal.
 * Captures the live screenshot at failure time (not the old recording screenshot).
 * This provides accurate debugging context showing the actual state when the operation failed.
 *
 * Only populated when:
 * - Operation fails during executor-initiated runs (not recorder)
 * - Operation is eligible for auto-heal
 */
export const zErroredAutoHealableOperation = z.object({
  originalOperationId: z.string(),
  stepId: z.string(),
  order: z.number(), // operation order within the step for correct positioning
  operationDescription: z.string(),
  error: z.string(),
  screenshotUrl: z.string().optional(), // R2 URL of live screenshot when it failed (optional if capture failed)
});

export const zTestRunExecutionOutput = z.object({
  aiChanges: z.array(zAiChange).default([]),
  errors: z.array(zExecutionError).default([]),
  logs: z.array(zExecutionLog).default([]),
  conditionalBlockResults: z.array(zConditionalBlockResult).default([]),
  erroredAutoHealableOperations: z.array(zErroredAutoHealableOperation).default([]),
  aiActionLogs: z.array(zAiActionLog).default([]),
  autohealSuggestion: zAutohealSuggestion.optional(),
});

export type FailedAssertion = {
  id: string;
  status: 'unresolved' | 'resolved';
  stepId: string;
  operationId: string;
  stepTitle: string;
  type: 'screenshot';
  // if screenshot, these are the url
  actual: string;
  expected: string;
  diff: string;
};

export type PossibleFlowMatch = {
  autoUserFlowId: string;
  name: string;
  // Similarity score
  score: number;
};

export type RecordingMatch = {
  sessionId: string;
  dateStr: string;
  pattern: Pattern[];
};

export type AutohealHistoryStats = z.output<typeof zAutohealHistoryStats>;

export type AutohealSuggestionDecision = z.output<typeof zAutohealSuggestionDecision>;

export type AutohealSuggestion = z.output<typeof zAutohealSuggestion>;

export type PoolClaim = {
  // We add a id since a user can decide to open multiple tabs and start multiple recorders
  id: string;
  userId: string;
};

export type TestRunExecutionOutput = z.output<typeof zTestRunExecutionOutput>;
export type AiChange = z.output<typeof zAiChange>;
export type conditionalBlockResult = z.output<typeof zConditionalBlockResult>;
export type AiActionLog = z.output<typeof zAiActionLog>;

export type JsonValue = string | number | boolean | JsonObject | JsonArray | null;

export type VariableOverrides = Record<
  string,
  {
    value: JsonValue;
    sensitive: boolean;
  }
>;

export type BoundingBoxCoordinates = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TaskExecutionContext = {
  region: Region;
  clusterName: string;
  taskId: string;
};

export const zR2BucketPath = z.object({
  bucket: z.string(),
  key: z.string(),
});

export type R2BucketPath = z.infer<typeof zR2BucketPath>;

export type HeaderContent = {
  value: string;
  filter?: string;
};

export type PlaywrightPrecalculatedStats = {
  total: {
    totalTests: number;
    totalSpecs: number;
  };

  projects: Record<
    string,
    {
      totalTests: number;
      totalSpecs: number;
    }
  >;
};

export type Variable = z.output<typeof zVariable>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type PlaywrightOperationOptions = Record<string, any>;

declare global {
  namespace PrismaJson {
    type VariableExtraction = EmailVariableExtraction;

    type ReceiveEmailFilters = EmailFilters;

    type DomainOverrides = UrlReplacement[];

    type RecordingMatches = RecordingMatch[];

    type PossibleFlowMatches = PossibleFlowMatch[];

    type FailedAssertions = FailedAssertion[];

    type ExecutionOutput = TestRunExecutionOutput;

    type CachedOperations = OperationData[];

    type RequestHeaders = FetchRequestHeaders;

    type BoundingBox = BoundingBoxCoordinates;

    type Variables = VariablesType;

    type VariableOverridesMap = VariableOverrides;

    type ExecutionContext = TaskExecutionContext;

    type BucketPath = R2BucketPath;

    type PlaywrightSuiteTriageReport = SuiteTriageReportWithPrompt;

    type PlaywrightSuiteAutoHealReport = SuiteAutoHealReport;

    type AutohealProgressState = AutohealProgressStateType;

    type PlaywrightProjectSettings = FullConfig;

    type PlaywrightConfig = PlaywrightTestConfig;

    type EvaluatedConfig = EvaluatedConfigType;

    type PlaywrightStats = PlaywrightPrecalculatedStats;

    export type StorageState = {
      cookies: {
        name: string;
        value: string;
        domain: string;
        path: string;
        expires: number;
        httpOnly: boolean;
        secure: boolean;
        sameSite: 'Strict' | 'Lax' | 'None';
      }[];
      origins: {
        origin: string;
        localStorage: {
          name: string;
          value: string;
        }[];
      }[];
    };
    export type OperationOptions = PlaywrightOperationOptions;

    export type Target = TargetSchema;

    // TODO: we probably can merge BrowserSettings and BrowserConfig into one thing at some point.
    // We probably need to wait until the user can configure multple browser settings for a project.
    type BrowserSettings = {
      headers?: Record<string, HeaderContent>;
    };

    type DashboardFilters = DashboardFilterType;

    type PendingFile = { path: string; content: string };
  }
}
